import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const ACTION_META = {
  created: { label: 'Created', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  updated: { label: 'Edited', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  restocked: { label: 'Restocked', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  consumed: { label: 'Consumed', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  deleted: { label: 'Deleted', cls: 'bg-red-100 text-red-800 border-red-200' },
  serialized: { label: 'Serialized', cls: 'bg-violet-100 text-violet-800 border-violet-200' },
  unserialized: { label: 'Serial removed', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const FIELD_LABEL = {
  part_number: 'Part Number',
  name: 'Name',
  description: 'Description',
  quantity: 'Quantity',
  unit: 'Unit',
  location: 'Location',
  reorder_level: 'Reorder Level',
  unit_cost: 'Unit Cost',
  supplier: 'Supplier',
  notes: 'Notes',
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString();
};

const formatVal = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

const InventoryAuditDialog = ({ open, onClose, item }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && item) {
      fetchAudit();
    }
  }, [open, item]);

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/${item.id}/audit`);
      setEntries(res.data);
    } catch (err) {
      toast.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="inventory-audit-dialog">
        <DialogHeader>
          <DialogTitle>
            Activity History — <span className="font-mono">{item.part_number}</span>{' '}
            <span className="text-slate-500 font-normal">({item.name})</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-500">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="py-8 text-center text-slate-500" data-testid="audit-empty">
            No activity recorded yet for this part.
          </div>
        ) : (
          <ol className="relative border-l border-slate-200 pl-6 space-y-5 mt-2" data-testid="audit-timeline">
            {entries.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
              const hasQty = e.qty_before !== null && e.qty_before !== undefined && e.qty_after !== null && e.qty_after !== undefined;
              return (
                <li key={e.id} className="ml-2" data-testid={`audit-entry-${e.id}`}>
                  <span className={`absolute -left-2 mt-1.5 h-3 w-3 rounded-full border-2 border-white ring-1 ring-slate-200 ${
                    e.action === 'consumed' ? 'bg-amber-500'
                    : e.action === 'deleted' ? 'bg-red-500'
                    : e.action === 'created' || e.action === 'restocked' ? 'bg-emerald-500'
                    : e.action === 'serialized' ? 'bg-violet-500'
                    : e.action === 'unserialized' ? 'bg-slate-400'
                    : 'bg-blue-500'
                  }`} />
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant="outline" className={`${meta.cls} border`}>{meta.label}</Badge>
                    {hasQty && e.qty_delta !== 0 && (
                      <span className={`text-sm font-medium ${e.qty_delta < 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {e.qty_delta > 0 ? '+' : ''}{e.qty_delta}
                        <span className="text-slate-500 font-normal"> · {e.qty_before} → {e.qty_after}</span>
                      </span>
                    )}
                    <span className="text-xs text-slate-500 ml-auto">{formatDate(e.timestamp)}</span>
                  </div>
                  <div className="text-sm text-slate-700">
                    {e.reason || meta.label}
                  </div>
                  {e.field_changes && e.field_changes.length > 0 && (
                    <ul className="mt-1.5 text-xs text-slate-600 bg-slate-50 rounded border border-slate-100 px-3 py-2 space-y-0.5">
                      {e.field_changes.map((fc, idx) => (
                        <li key={idx}>
                          <span className="font-medium">{FIELD_LABEL[fc.field] || fc.field}:</span>{' '}
                          <span className="line-through text-slate-400">{formatVal(fc.before)}</span>
                          {' → '}
                          <span className="text-slate-800">{formatVal(fc.after)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="text-xs text-slate-500 mt-1">
                    by <span className="font-medium">{e.user_name || 'Unknown'}</span>
                    {e.source === 'maintenance_log' && (
                      <span className="ml-2 italic">(from maintenance log)</span>
                    )}
                    {e.source === 'serialized_component' && e.source_ref_id && (
                      <Link to={`/inventory/serialized/${e.source_ref_id}`} onClick={onClose} className="ml-2 underline text-blue-700"
                        data-testid={`audit-serial-link-${e.id}`}>View serialized part</Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InventoryAuditDialog;
